import { Component, h } from "preact";
import { AssetManager } from "../../asset_manager/asset_manager";
import { GalleryImage, ImageSource } from "../../gallery/gallery_image";
import { assert } from "../../utils/asserts";
import { delay } from "../../utils/async";
import { Debouncer } from "../../utils/debouncer";
import { AppContext } from "../app_context";

import css from "./image.scss";
import { imageManager } from "./image_manager";

const LOW_RES_THRESHOLD = 50;

interface Props {
  image: GalleryImage;
  preloadWidth?: number;
  onInitialLoad?: () => void;
  debug?: boolean;
}

interface SourceLoadingStage {
  source: ImageSource;
  lowResLoading: boolean;
  id: number;
  timestamp: number;
}

interface State {
  currentSource: SourceLoadingStage;
  loadingSource?: SourceLoadingStage;
  transitioningOutSource?: SourceLoadingStage;
}

export class Image extends Component<Props, State> {
  private containerEl: HTMLElement | null = null;
  private sortedSources: ImageSource[] | null = null;
  private resizeObserver?: ResizeObserver;
  private resizeDebouncer = new Debouncer(
    () => {
      this.maybeUpdateImageSource();
    },
    { waitMs: 500 }
  );
  private initialLoaded = false;
  private mounted = false;

  constructor(props: Props) {
    super(props);

    this.handleImageUpdate({ updateSource: false });
    const { bestLoadedSource, bestSource } = this.getBestSource();

    this.state = {
      currentSource: {
        source: bestLoadedSource!,
        id: 0,
        lowResLoading: bestLoadedSource!.width <= LOW_RES_THRESHOLD,
        timestamp: performance.now(),
      },
      loadingSource:
        bestSource && bestSource.url !== bestLoadedSource!.url
          ? {
              source: bestSource,
              id: 1,
              lowResLoading: false,
              timestamp: performance.now(),
            }
          : undefined,
    };

    if (BUILD_PLATFORM === "browser") {
      this.resizeObserver = new ResizeObserver(() => {
        this.resizeDebouncer.fire();
      });
    }
  }

  render() {
    const { currentSource, loadingSource, transitioningOutSource } = this.state;
    const { image } = this.props;

    let lowResLoading = currentSource?.lowResLoading;

    const x = (
      <AppContext.Consumer>
        {(appContext) => {
          assert(appContext);
          const { assetManager } = appContext;
          const currentImg = this.makeImg(assetManager, image, currentSource, "current");
          const loadingImg = this.makeImg(assetManager, image, loadingSource, "pending");
          const transitioningOutImg = this.makeImg(
            assetManager,
            image,
            transitioningOutSource,
            "transition-out"
          );
          return [loadingImg, currentImg, transitioningOutImg].filter((item) => item);
        }}
      </AppContext.Consumer>
    );

    return (
      <div
        class={css("container", { "low-res-loading": !!lowResLoading })}
        ref={this.setContainerEl}
      >
        {x}
      </div>
    );
  }

  private makeImg(
    assetManager: AssetManager,
    image: GalleryImage,
    source: SourceLoadingStage | undefined,
    type: "pending" | "current" | "transition-out"
  ) {
    if (!source) return null;

    const url = assetManager.getUrl(source.source.url);
    return (
      <img
        key={"image-" + source.id}
        src={url}
        class={css("img", {
          pending: type === "pending",
          "transition-out": type === "transition-out",
        })}
        onLoad={type === "pending" ? this.handlePendingImageLoad : this.handleCurrentImageLoad}
        alt={image.alt}
      />
    );
  }

  private setContainerEl = (el: HTMLElement | null) => {
    const previousEl = this.containerEl;
    this.containerEl = el;

    const { resizeObserver } = this;
    if (!resizeObserver) return;

    if (previousEl) resizeObserver.unobserve(previousEl);
    if (el) {
      resizeObserver.observe(el);
    }

    if (!previousEl && el) this.maybeUpdateImageSource();
  };

  private maybeEmitInitialLoad() {
    if (this.initialLoaded) return;

    this.initialLoaded = true;
    const { onInitialLoad } = this.props;
    if (onInitialLoad) onInitialLoad();
  }

  private handleCurrentImageLoad = async () => {
    if (!this.mounted) return;

    this.maybeEmitInitialLoad();
  };

  private handlePendingImageLoad = async () => {
    if (!this.mounted) return;

    this.maybeEmitInitialLoad();

    const { loadingSource } = this.state;
    if (!loadingSource) return;

    imageManager.setSourceLoaded(loadingSource.source);

    this.setState((prevState) => {
      if (loadingSource !== prevState.loadingSource) return null;
      const animate =
        prevState.currentSource && performance.now() - prevState.currentSource.timestamp > 100;
      return {
        transitioningOutSource: animate ? prevState.currentSource : undefined,
        currentSource: prevState.loadingSource,
        loadingSource: undefined,
        lowResLoading: false,
      };
    });

    // wait for animation to finish
    await delay(500);
    if (!this.mounted) return;

    this.setState((prevState) => {
      return {
        transitioningOutSource: undefined,
      };
    });
  };

  private getBestSource(): {
    bestSource: ImageSource;
    bestLoadedSource?: ImageSource;
    isPartial: boolean;
  } {
    const { containerEl, sortedSources } = this;

    // Find the best source for current size
    const width = containerEl?.clientWidth ?? this.props.preloadWidth ?? 0;

    let bestSource: ImageSource | null = null;
    let bestLoadedSource: ImageSource | null = null;
    let bestSourceFound = false;
    for (const source of sortedSources!) {
      let isLoaded = imageManager.isLoaded(source);

      // Keep going if current best source is still too small
      if (!bestSource || bestSource.width < width) {
        bestSource = source;
      }

      if (isLoaded && (!bestLoadedSource || bestLoadedSource.width < width)) {
        bestLoadedSource = source;
      }

      if (source.width > width) {
        // The smallest source whose width is larger than the element
        bestSourceFound = true;
      }
    }

    return {
      bestSource: bestSource!,
      bestLoadedSource: bestLoadedSource!,
      isPartial: width <= 0,
    };
  }

  private maybeUpdateImageSource() {
    let { bestSource, bestLoadedSource, isPartial } = this.getBestSource();

    if (bestLoadedSource) {
      this.maybeUseSource(bestLoadedSource);
    }

    this.maybeLoadSource(bestSource);
  }

  private maybeUseSource(source: ImageSource) {
    const { currentSource } = this.state;
    if (source.url !== currentSource.source.url && source.width > currentSource.source.width) {
      this.setState((prevState) => {
        return {
          currentSource: {
            source: source!,
            id: currentSource.id,
            lowResLoading: false,
            timestamp: performance.now(),
          },
        };
      });
    }
  }

  private maybeLoadSource(source: ImageSource) {
    const { currentSource, loadingSource } = this.state;
    if (
      source.url !== currentSource.source.url &&
      source.width > currentSource.source.width &&
      (!loadingSource || source.url !== loadingSource.source.url)
    ) {
      this.setState((prevState) => {
        return {
          loadingSource: {
            source: source!,
            id: Math.max(currentSource.id, loadingSource?.id ?? 0) + 1,
            lowResLoading: false,
            timestamp: performance.now(),
          },
        };
      });
    }
  }

  private handleImageUpdate({ updateSource = true } = {}) {
    const { image } = this.props;
    const sortedSources = [...image.sources];
    sortedSources.sort((a, b) => a.width - b.width);
    this.sortedSources = sortedSources;

    // TODO: set lowResLoading to true again and use an initial source
    if (updateSource) {
      this.maybeUpdateImageSource();
    }
  }

  componentDidMount() {
    this.mounted = true;
    this.maybeUpdateImageSource();
  }

  componentDidUpdate(prevProps: Props) {
    const { props } = this;
    if (prevProps.image !== props.image) {
      /*
      this.handleImageUpdate();
      const { bestLoadedSource, bestSource } = this.getBestSource();

      this.setState({
        currentSource: {
          source: bestLoadedSource!,
          id: 0,
          lowResLoading: bestLoadedSource!.width <= LOW_RES_THRESHOLD,
          timestamp: performance.now(),
        },
        loadingSource:
          bestSource && bestSource.url !== bestLoadedSource!.url
            ? {
                source: bestSource,
                id: 1,
                lowResLoading: false,
                timestamp: performance.now(),
              }
            : undefined,
      });
      */
    }
  }

  componentWillUnmount() {
    const { resizeObserver } = this;
    this.mounted = false;
    if (resizeObserver) resizeObserver.disconnect();
  }
}
